// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// DateInput — one date field for tables and forms (ISO YYYY-MM-DD in and out; shown as MM/DD/YYYY).
//
// TYPED, NOT SEGMENTED (ASP 2026-10-15, owner: "date picker still does not allow type"). This used to be a
// native <input type="date">. Chrome's date field is three segments; a click lands in whichever segment
// was under the pointer (often the day or year), so typing 11202026 filled the wrong segments, the date
// stayed incomplete, and on leaving the field it was thrown away — to the user, typing simply did not work.
// It is now a plain text box: type 11202026, 11/20/2026 or 2026-11-20 wherever you click; the slashes
// appear as you type. The calendar button still opens the browser's date picker.
//
// It never commits per keystroke: it commits once the field has been still for COMMIT_DELAY_MS — which is
// also how a date picked from the calendar saves without another click — or at once on leaving it or on
// Enter. Only a complete, real date (or a field emptied and then left) is ever committed. An incomplete
// date is NOT thrown away on leaving: it stays on screen, outlined red, until it is finished or cleared.
// Each value is sent ONCE (review 2026-10-02, S7): the pause timer firing and then a blur before the
// save's reload returned used to send it twice — two PUTs, two audit rows. When the caller's save
// returns a promise that rejects, the field goes back to the saved date (S8) rather than showing a
// date that was never stored.
//
// ADD-WEEKS HELPER (D-CRX-92 §6, ASP #16 "Target date: today + 48 weeks"): with `offsetHelper` set, a
// small "+wk" button opens a popover where the user enters N weeks; the field is FILLED with base + 7N
// days (base = `offsetHelper.base`, else today) and committed like a calendar pick. The result is an
// ordinary date the user can still edit; nothing about "48 weeks" is stored. Without the prop the field
// renders exactly as before.

import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { CalendarDays } from 'lucide-react'
import Popover from '@mui/material/Popover'
import AppButton from './AppButton'
import { AppTextField } from './FormFields'

export const COMMIT_DELAY_MS = 900

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/
/** Marks a draft that is being typed but is not a date yet — never committable. */
const PARTIAL = 'partial'
/** MM/DD/YYYY is ten characters; the box takes no more. */
const MAX_TYPED = 10

/** A value worth committing: empty (cleared), or a full date with a plausible year. */
export function committable(v: string): boolean {
  if (v === '') return true
  const m = ISO.exec(v)
  return !!m && Number(m[1]) >= 1900
}

const pad = (n: number) => String(n).padStart(2, '0')
function realDate(y: number, m: number, d: number): string | undefined {
  if (y < 1900 || y > 2199 || m < 1 || m > 12 || d < 1) return undefined
  const dt = new Date(Date.UTC(y, m - 1, d))
  return dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d ? `${y}-${pad(m)}-${pad(d)}` : undefined
}

/** What the box holds, as ISO: '' when empty, the date when complete and real, undefined otherwise. */
export function parseTypedDate(text: string): string | undefined {
  const t = text.trim()
  if (t === '') return ''
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t)
  if (m) return realDate(+m[1], +m[2], +m[3])
  m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(t)
  if (m) return realDate(+m[3], +m[1], +m[2])
  m = /^(\d{2})(\d{2})(\d{4})$/.exec(t)
  if (m) return realDate(+m[3], +m[1], +m[2])
  return undefined
}

/** Today on the user's own calendar, as ISO (not the UTC date, which is tomorrow late in the evening). */
export function localTodayIso(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** The calendar day of an ISO date or date-time (YYYY-MM-DD or YYYY-MM-DDT…). */
const DAY_OF = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/

/** base + 7×weeks days, date-only: the arithmetic runs on a UTC calendar date, so a DST change between
 *  the two dates cannot shift the result by a day. undefined when the base is not a date. */
export function addWeeksIso(base: string, weeks: number): string | undefined {
  const m = DAY_OF.exec(base)
  if (!m || !Number.isInteger(weeks)) return undefined
  const dt = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3] + weeks * 7))
  return realDate(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate())
}

/** Largest offset the helper takes (10 years). */
export const MAX_OFFSET_WEEKS = 520

/** ISO → what the box shows. */
export const showDate = (iso: string) => {
  const m = ISO.exec(iso)
  return m ? `${m[2]}/${m[3]}/${m[1]}` : iso
}

/** Tidies what was typed: digits become MM/DD/YYYY as they arrive (the slashes are ours); an ISO date or a
 *  one-digit month/day typed with its own separators (1/5/2026) is kept as typed. */
export function maskTyped(raw: string): string {
  const cleaned = raw.replace(/[^\d/.-]/g, '')
  const parts = cleaned.split(/[/.-]/)
  if (/^\d{4}-/.test(cleaned) || (parts.length > 1 && parts[0].length === 1) || (parts.length > 2 && parts[1].length === 1))
    return cleaned.slice(0, MAX_TYPED)
  const d = cleaned.replace(/\D/g, '').slice(0, 8)
  return d.length <= 2 ? d : d.length <= 4 ? `${d.slice(0, 2)}/${d.slice(2)}` : `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`
}

/** The commit rules, without React — so they can be tested with fake timers. */
export function createDateCommitter(io: {
  draft: () => string
  saved: () => string
  setDraft: (v: string) => void
  fire: (iso: string | null) => void | Promise<unknown>
}) {
  let timer: ReturnType<typeof setTimeout> | null = null
  let sent: string | null = null   // sent, and the saved value has not caught up yet
  const cancel = () => { if (timer) { clearTimeout(timer); timer = null } }
  const commit = () => {
    cancel()
    const d = io.draft(), sv = io.saved()
    if (d === sv || d === sent) return
    if (!committable(d)) { io.setDraft(sv); return }   // an incomplete date goes back to the saved one
    sent = d
    const pending = io.fire(d || null)
    if (pending && typeof (pending as Promise<unknown>).then === 'function')
      (pending as Promise<unknown>).catch(() => {
        if (sent !== d) return            // a newer value is already on its way
        sent = null
        io.setDraft(io.saved())
      })
  }
  const edit = (v: string) => {
    io.setDraft(v)
    cancel()
    // Only a complete date saves on a pause; a clear is committed only on leaving the field or on Enter.
    if (v !== '' && committable(v)) timer = setTimeout(commit, COMMIT_DELAY_MS)
  }
  /** The saved value changed (the save landed, or another user's did): nothing is in flight. */
  const savedChanged = () => { sent = null }
  /** Leaving a HALF-typed date: nothing is sent (the field keeps the saved value underneath). */
  const leavePartial = () => { cancel(); io.setDraft(io.saved()) }
  return { edit, commit, cancel, savedChanged, leavePartial }
}

export default function DateInput({ value, onChange, label, disabled, emphasis, title, style: extra, offsetHelper }: {
  value?: string | null
  /** The new ISO date, or null when the field is cleared. Called once per committed change; return
   *  the save's promise to have the field revert when it fails. */
  onChange: (iso: string | null) => void | Promise<unknown>
  /** What the date is, for screen readers ("Follow-up date"). */
  label: string
  disabled?: boolean
  /** 'overdue' draws the field in the warning colour (a due date already past). */
  emphasis?: 'overdue'
  title?: string
  /** Layout tweaks from the caller (width, a highlighted border); merged over the kit look. */
  style?: CSSProperties
  /** Adds the "+N weeks" helper. `base` is the ISO date the weeks count from; absent or empty = today. */
  offsetHelper?: { base?: string | null }
}) {
  const saved = value ? value.slice(0, 10) : ''
  const [text, setText] = useState(showDate(saved))
  const [invalid, setInvalid] = useState(false)
  const picker = useRef<HTMLInputElement>(null)
  const textRef = useRef(text)            // what the box holds right now (state lags a render)
  // Latest values for the timer callback (a timeout would otherwise read a stale render).
  const live = useRef({ draft: saved, saved, onChange }); live.current = { ...live.current, saved, onChange }
  const committer = useRef<ReturnType<typeof createDateCommitter> | null>(null)
  if (!committer.current) committer.current = createDateCommitter({
    draft: () => live.current.draft,
    saved: () => live.current.saved,
    // A revert (failed save, or back to the saved date) also puts the saved date back in the box.
    setDraft: v => {
      const typing = v === PARTIAL || v === parseTypedDate(textRef.current)
      live.current.draft = v
      if (!typing) { setText(showDate(v)); textRef.current = showDate(v); setInvalid(false) }
    },
    fire: iso => live.current.onChange(iso),
  })
  const { edit, commit, cancel, savedChanged } = committer.current
  const type = (raw: string) => {
    const t = maskTyped(raw)
    textRef.current = t; setText(t); setInvalid(false)
    const iso = parseTypedDate(t)
    edit(iso === undefined ? PARTIAL : iso)
  }
  // Leaving the field: a complete date (or a cleared one) is committed; an unfinished one stays on screen,
  // outlined red, and nothing is sent.
  const leave = () => {
    if (parseTypedDate(textRef.current) === undefined) { cancel(); setInvalid(true); return }
    commit()
  }
  const pick = (iso: string) => {
    if (!iso) return
    textRef.current = showDate(iso); setText(showDate(iso)); setInvalid(false)
    edit(iso); commit()
  }
  useEffect(() => {
    savedChanged(); live.current.draft = saved
    textRef.current = showDate(saved); setText(showDate(saved)); setInvalid(false)
  }, [saved, savedChanged])
  useEffect(() => cancel, [cancel])

  const warn = emphasis === 'overdue'
  const box: CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 2, position: 'relative', boxSizing: 'border-box',
    fontSize: 12, padding: '2px 4px 2px 6px', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)',
    border: `1px solid ${warn ? '#d97706' : 'var(--color-border)'}`,
    fontWeight: warn ? 700 : undefined,
    opacity: disabled ? 0.45 : undefined,   // a field that cannot be used looks it (it replaced inputs that did)
    ...extra,
    ...(invalid ? { border: '1px solid #dc2626', boxShadow: '0 0 0 2px rgba(220,38,38,.15)' } : null),
  }
  return (
    <span style={box} title={invalid ? 'Finish the date as MM/DD/YYYY, or clear it' : title}>
      <input type="text" inputMode="numeric" autoComplete="off" aria-label={label} aria-invalid={invalid || undefined}
        placeholder="MM/DD/YYYY" disabled={disabled} value={text} maxLength={10}
        style={{ border: 'none', outline: 'none', background: 'transparent', color: 'inherit', font: 'inherit',
                 fontWeight: 'inherit', padding: '2px 0', width: '100%', minWidth: '10ch', flex: 1 }}
        onChange={e => type(e.target.value)}
        onBlur={leave}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); leave() } }} />
      <button type="button" aria-label="Open calendar" title="Open calendar" disabled={disabled} tabIndex={-1}
        onClick={() => { const p = picker.current; if (!p) return; try { p.showPicker() } catch { p.focus(); p.click() } }}
        style={{ border: 'none', background: 'transparent', padding: 2, cursor: disabled ? 'default' : 'pointer',
                 color: 'var(--color-text-mute)', display: 'inline-flex' }}>
        <CalendarDays size={14} aria-hidden="true" />
      </button>
      {offsetHelper && <AddWeeks label={label} disabled={disabled} base={offsetHelper.base} onPick={pick} />}
      {/* The browser's own calendar, opened by the button above; never typed into. */}
      <input ref={picker} type="date" tabIndex={-1} aria-hidden="true" disabled={disabled}
        value={committable(live.current.draft) ? live.current.draft : saved}
        onChange={e => pick(e.target.value)}
        style={{ position: 'absolute', right: 0, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: 'none', border: 0, padding: 0 }} />
    </span>
  )
}

/** The "+wk" button and its popover: N weeks → base + 7N days, handed to the field as a calendar pick. */
function AddWeeks({ label, disabled, base, onPick }: {
  label: string; disabled?: boolean; base?: string | null; onPick: (iso: string) => void
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [weeks, setWeeks] = useState('')
  const day = base ? DAY_OF.exec(base) : null
  const from = day ? `${day[1]}-${day[2]}-${day[3]}` : ''
  const fromText = from ? showDate(from) : 'today'
  const n = Number(weeks)
  const ok = /^\d+$/.test(weeks) && n >= 1 && n <= MAX_OFFSET_WEEKS
  const result = ok ? addWeeksIso(from || localTodayIso(), n) : undefined
  const close = () => { setAnchor(null); setWeeks('') }
  const apply = () => { if (!result) return; onPick(result); close() }
  return (
    <>
      <button type="button" aria-label={`Add weeks to ${label}`} title={`Set to a number of weeks from ${fromText}`} disabled={disabled}
        onClick={e => setAnchor(e.currentTarget)}
        style={{ border: 'none', background: 'transparent', padding: '2px 3px', cursor: disabled ? 'default' : 'pointer',
                 color: 'var(--color-text-mute)', font: 'inherit', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
        +wk
      </button>
      <Popover open={!!anchor} anchorEl={anchor} onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8, width: 220 }}>
          <AppTextField size="small" label="Weeks" value={weeks} autoFocus
            inputProps={{ inputMode: 'numeric', 'aria-label': 'Number of weeks' }}
            onChange={e => setWeeks(e.target.value.replace(/\D/g, '').slice(0, 3))}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); apply() } }}
            helperText={result ? `${showDate(result)} (${n} wk from ${fromText})` : `1–${MAX_OFFSET_WEEKS} weeks from ${fromText}`} />
          <AppButton size="small" variant="contained" disabled={!result} onClick={apply}>Set date</AppButton>
        </div>
      </Popover>
    </>
  )
}
